import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getProductBySlug, PRODUCTS, getCategoryById } from '../data/products';
import { COMPANY_INFO } from '../data/company';
import { QuoteForm } from '../components/common/QuoteForm';
import { ProductCard } from '../components/common/ProductCard';
import Seo from '../components/common/Seo';
import { getRouteMeta } from '../routeMeta';
import { getProductFaqs } from '../data/supportFaqs';

const ProductDetailPage = () => {
  const { productSlug } = useParams();
  const product = getProductBySlug(productSlug);
  const productFaqs = getProductFaqs(productSlug);
  const seoPath = `/products/p/${productSlug}`;

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Seo {...getRouteMeta(seoPath)} path={seoPath} />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#0B1F3B] mb-4">Product Not Found</h1>
          <p className="text-slate-500 mb-8">The product you're looking for doesn't exist.</p>
          <Link to="/products">
            <Button className="bg-[#0B1F3B] hover:bg-[#1E40AF] text-white">
              View All Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const category = getCategoryById(product.category);
  const relatedProducts = PRODUCTS.filter(p => p.category === product.category && p.id !== product.id).slice(0, 3);
  const productImageFit = product.imageFit === 'contain' ? 'object-contain p-6' : 'object-cover';
  const phoneNumbers = COMPANY_INFO.contact.phone || [];

  return (
    <div data-testid="product-detail-page">
      <Seo {...getRouteMeta(seoPath)} path={seoPath} />
      {/* Breadcrumb */}
      <section className="bg-slate-50 border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-[#0B1F3B]">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-[#0B1F3B]">Products</Link>
            <span>/</span>
            <Link to={`/products/${product.category}`} className="hover:text-[#0B1F3B]">{category?.name || 'Category'}</Link>
            <span>/</span>
            <span className="text-[#0B1F3B] font-medium">{product.name}</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="sticky top-24">
                <div className="overflow-hidden border border-white/10 bg-[radial-gradient(circle_at_top,rgba(96,165,250,0.14),transparent_42%),linear-gradient(180deg,#112448_0%,#0B1730_100%)]">
                  <img 
                    src={product.image}
                    alt={product.name}
                    loading="eager"
                    className={`media-grade aspect-[4/3] w-full ${productImageFit}`}
                  />
                </div>
                <div className="mt-6 p-6 bg-[#0B1F3B] text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <Phone className="h-5 w-5 text-[#93C5FD]" />
                    <span className="text-sm uppercase tracking-wider">Quick Contact</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {phoneNumbers.map((phone) => (
                      <a
                        key={phone}
                        href={`tel:${phone}`}
                        className="text-2xl font-bold transition-colors hover:text-[#93C5FD]"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                  <p className="text-slate-300 mt-2 text-sm">
                    Speak to our experts for custom requirements
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-sm font-mono text-[#1E40AF] uppercase tracking-widest">
                {category?.name}
              </span>
              <h1 className="font-['Manrope'] text-4xl md:text-5xl font-bold text-[#0B1F3B] mt-2 uppercase tracking-tight">
                {product.name}
              </h1>
              <p className="text-xl text-slate-600 mt-4">
                {product.tagline}
              </p>
              <p className="text-slate-500 mt-6 leading-relaxed">
                {product.description}
              </p>

              {/* Citable answers: always rendered so crawlers and AI engines can quote them */}
              {productFaqs.length > 0 && (
                <div className="mt-8 space-y-6" data-testid="product-faq-answers">
                  {productFaqs.map((faq) => (
                    <div key={faq.question} className="border-l-4 border-[#1E40AF] bg-slate-50 px-5 py-4">
                      <h2 className="font-['Manrope'] text-lg font-bold text-[#0B1F3B]">{faq.question}</h2>
                      <p className="mt-2 leading-relaxed text-slate-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Tabs */}
              <div className="mt-10">
                <Tabs defaultValue="specifications" className="w-full">
                  <TabsList className="flex h-auto w-full flex-col items-stretch justify-start rounded-none border border-white/10 bg-[#13243f] p-0 sm:flex-row sm:items-center">
                    <TabsTrigger 
                      value="specifications" 
                      className="w-full rounded-none px-4 py-4 text-sm font-semibold uppercase tracking-wider text-[#DBEAFE] data-[state=active]:border-b-2 data-[state=active]:border-[#60A5FA] data-[state=active]:bg-white data-[state=active]:text-[#06101f] sm:w-auto sm:px-6"
                      data-testid="tab-specifications"
                    >
                      Specifications
                    </TabsTrigger>
                    <TabsTrigger 
                      value="features" 
                      className="w-full rounded-none px-4 py-4 text-sm font-semibold uppercase tracking-wider text-[#DBEAFE] data-[state=active]:border-b-2 data-[state=active]:border-[#60A5FA] data-[state=active]:bg-white data-[state=active]:text-[#06101f] sm:w-auto sm:px-6"
                      data-testid="tab-features"
                    >
                      Features
                    </TabsTrigger>
                    <TabsTrigger 
                      value="scope" 
                      className="w-full rounded-none px-4 py-4 text-sm font-semibold uppercase tracking-wider text-[#DBEAFE] data-[state=active]:border-b-2 data-[state=active]:border-[#60A5FA] data-[state=active]:bg-white data-[state=active]:text-[#06101f] sm:w-auto sm:px-6"
                      data-testid="tab-scope"
                    >
                      Scope of Supply
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="specifications" className="mt-6">
                    <div className="overflow-hidden border border-white/15 bg-[#13243f]">
                      <table className="w-full table-fixed">
                        <tbody>
                          {Object.entries(product.specifications).map(([key, value], index) => (
                            <tr key={key} className={index % 2 === 0 ? 'bg-white/[0.07]' : 'bg-white/[0.03]'}>
                              <td className="w-[42%] break-words border-r border-white/15 px-4 py-4 text-sm font-bold uppercase tracking-wider text-white sm:w-1/3 sm:px-6">
                                {key}
                              </td>
                              <td className="break-words px-4 py-4 font-['JetBrains_Mono'] text-sm font-semibold text-[#DBEAFE] sm:px-6">
                                {value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>

                  <TabsContent value="features" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {product.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3 border-l-4 border-[#60A5FA] bg-white/[0.07] p-4">
                          <CheckCircle className="h-5 w-5 text-[#60A5FA] flex-shrink-0 mt-0.5" />
                          <span className="text-[#E2E8F0]">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="scope" className="mt-6">
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-bold text-[#0B1F3B] mb-4 uppercase tracking-wider text-sm">Standard Scope of Supply</h4>
                        <ul className="space-y-2">
                          {product.scopeOfSupply.map((item, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <CheckCircle className="h-5 w-5 text-[#60A5FA] flex-shrink-0 mt-0.5" />
                              <span className="text-[#E2E8F0]">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0B1F3B] mb-4 uppercase tracking-wider text-sm">Optional Accessories</h4>
                        <ul className="space-y-2">
                          {product.optionalAccessories.map((item, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="h-5 w-5 text-[#93C5FD] flex-shrink-0">+</span>
                              <span className="text-[#E2E8F0]">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Applications */}
              <div className="mt-10">
                <h4 className="font-bold text-[#0B1F3B] mb-4 uppercase tracking-wider text-sm">Applications</h4>
                <div className="flex flex-wrap gap-2">
                  {product.applications.map((app, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#0B1F3B]/5 text-[#0B1F3B] text-sm font-medium"
                    >
                      {app}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact#quote-form">
                  <Button className="tech-shimmer rounded-sm bg-[#1E40AF] px-8 py-5 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] hover:bg-[#2563EB]" data-testid="product-quote-btn">
                    Request Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-sm font-mono text-[#1E40AF] uppercase tracking-widest">Get a Quote</span>
              <h2 className="font-['Manrope'] text-3xl md:text-4xl font-bold text-[#0B1F3B] mt-4 uppercase tracking-tight">
                Interested in {product.name}?
              </h2>
              <p className="mt-6 text-slate-600">
                Fill out the form and our team will get back to you within 24 hours with a detailed proposal tailored to your requirements.
              </p>
            </div>
            <div className="bg-white p-8 border border-slate-200">
              <QuoteForm preselectedProduct={product.id} compact />
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="text-sm font-mono text-[#1E40AF] uppercase tracking-widest">Related</span>
                <h2 className="font-['Manrope'] text-3xl font-bold text-[#0B1F3B] mt-2 uppercase tracking-tight">
                  Similar Products
                </h2>
              </div>
              <Link to={`/products/${product.category}`}>
                <Button variant="outline" className="border-2 border-[#0B1F3B] text-[#0B1F3B] hover:bg-[#0B1F3B] hover:text-white font-semibold uppercase tracking-wider" data-testid="view-category-btn">
                  View All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedProducts.map((p, index) => (
                <ProductCard key={p.id} product={p} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetailPage;
